import { FeatureStorageQueryStringParamsAndStorageKey } from '../../Common/Constants/QueryString_Keys.Constant';
import { isEmptyOrSpaces } from '../../Utilities/Helpers';
import BrowserStorage from './BrowserStorage';
import { QueryStringParserService } from './QueryStringParserService';

/**
 * This class handles the storage of variables into arrays using SessionStorage
 *
 * @queryString = string from url
 * @storageKey: string used as id in SessionStorage
 * @featureName: string to be added or removed from array that matches storageKey
 *
 * FeatureStorageService is called in DashboardApp constructor to avoid lossing queryParams due to url parsing.
 * It searches for the queryStrings and storageKeys defined in FeatureStorageQueryStringParamsAndStorageKey
 * at the Constants file, then if it complies with the following structure they are added or removed.
 * Structure:
 * {queryString}={featureName}:{1/0};{featureName1}:{1/0}&{queryString1}={featureName2}:{1/0};{featureName3}:{1/0}
 *
 * 1 = ENABLE
 * 2 = DISABLE
 *
 * This class handle automatically the search for the queryStrings and storageKeys that are defined in
 * the dictionary previously mentioned. To add a new one follow the structure:
 *
 * {queryString} : {storageKey}
 * */

export default class FeatureStorageService {
  protected static instance: FeatureStorageService;

  /** Browser Storage Service */
  protected browserStorage: BrowserStorage;

  public static getInstance = () => {
    if (!FeatureStorageService.instance) {
      FeatureStorageService.instance = new FeatureStorageService();
    }
    return FeatureStorageService.instance;
  };

  protected constructor() {
    this.browserStorage = BrowserStorage.getInstance();
  }

  /**
   * Process Query Strings and Enables/Disables the FeatureNames.
   * So in order to enable Feature:
   * {queryString}={featureName}:{status};{featureName1}:{status}&{queryString1}={featureName2}:{status};{featureName3}:{status}
   * Here status will be either 0 or 1
   */
  public processFeatureStorageQueryStrings = () => {
    const queryParams = QueryStringParserService.getQueryParameters(window.location.search);

    if (queryParams) {
      for (const [queryString, storageKey] of Object.entries(FeatureStorageQueryStringParamsAndStorageKey)) {
        if (queryParams[queryString]) {
          this.validateQueryStringAndSetFeature(storageKey, queryParams[queryString], this.modifyFeatureStorage);
        }
      }
    }
  };

  /**
   * Splits the query string into their features and status and calls
   * session storage to add or remove the feature aaccordingly
   * @param storageKey
   * @param queryStringResponse
   * @param modifyStorage
   */
  protected validateQueryStringAndSetFeature = (
    storageKey: string,
    queryStringResponse: string,
    modifyStorage: any
  ) => {
    if (!isEmptyOrSpaces(queryStringResponse)) {
      const splittedResponse = queryStringResponse.split(';');
      splittedResponse.forEach(statusResponse => {
        const splittedFeatureStatus = statusResponse.split(':');

        if (splittedFeatureStatus && splittedFeatureStatus.length === 2 && !isEmptyOrSpaces(splittedFeatureStatus[0])) {
          if (splittedFeatureStatus[1] === '1') {
            modifyStorage(storageKey, splittedFeatureStatus[0], true);
          } else if (splittedFeatureStatus[1] === '0') {
            modifyStorage(storageKey, splittedFeatureStatus[0], false);
          }
        }
      });
    }
  };

  /**
   * Modifies the Feature Storage
   * @param featureName Name of the feature to be added or modified
   * @param enable True asks to enable the feature and False disables the feature
   * @param storageKey Storage Key - This is represents the name for the browser storage key.
   */
  protected modifyFeatureStorage = (storageKey: string, featureName: string, enable: boolean) => {
    const featureCollection = this.getFeatureCollectionToStore(storageKey, featureName, enable);
    this.browserStorage.setItem(storageKey, featureCollection, true);
  };

  /**
   * Gets the Feature to be stored
   * @param featureName Name of the feature to be added or modified
   * @param enable True asks to enable the feature and False disables the feature
   * @param storageKey Storage Key - This is represents the name for the browser storage key.
   */
  protected getFeatureCollectionToStore = (storageKey: string, featureName: string, enable: boolean): string[] => {
    let featureCollection: string[] = [];

    const features = this.browserStorage.getItem(storageKey, true);

    if (features && Array.isArray(features)) {
      featureCollection = features;
    }

    const indexOfFeature = featureCollection.indexOf(featureName);
    if (indexOfFeature > -1 && !enable) {
      featureCollection.splice(indexOfFeature, 1);
    }

    if (indexOfFeature === -1 && enable) {
      featureCollection.push(featureName);
    }

    return featureCollection;
  };
}
